import { ToolItem } from '../types';

export interface ColorToolItem extends ToolItem {
  colorCategory: 'pickers-palettes' | 'converters' | 'accessibility' | 'css-ui' | 'mixing-shades';
  colorCategoryLabel: string;
  iconBgColor: string;
  iconTextColor: string;
  iconName: string;
}

export const COLOR_CATEGORIES_TABS = [
  { id: 'all', label: 'All Colour Tools', icon: 'Palette' },
  { id: 'pickers-palettes', label: 'Pickers & Palettes', icon: 'Pipette' },
  { id: 'converters', label: 'Color Converters', icon: 'RefreshCw' },
  { id: 'accessibility', label: 'Accessibility & Contrast', icon: 'Eye' },
  { id: 'css-ui', label: 'CSS & UI Generators', icon: 'Sparkles' },
  { id: 'mixing-shades', label: 'Shades & Mixing', icon: 'SlidersHorizontal' }
];

export const COLOR_TOOLS_DATA: ColorToolItem[] = [
  // --- PICKERS & PALETTES (8 Tools) ---
  {
    id: 'color-picker',
    name: 'Color Picker',
    description: 'Interactive HTML5 color picker with HEX, RGB, HSL values and instant copy.',
    colorCategory: 'pickers-palettes',
    colorCategoryLabel: 'Pickers & Palettes',
    iconBgColor: 'bg-amber-500/15',
    iconTextColor: 'text-amber-500',
    iconName: 'Pipette',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'palette-generator',
    name: 'Palette Generator',
    description: 'Generate harmonious color palettes using complementary, triadic, and analogous rules.',
    colorCategory: 'pickers-palettes',
    colorCategoryLabel: 'Pickers & Palettes',
    iconBgColor: 'bg-orange-500/15',
    iconTextColor: 'text-orange-500',
    iconName: 'Palette',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'random-color',
    name: 'Random Color',
    description: 'Generate beautiful random colors with HEX, RGB values and instant copy.',
    colorCategory: 'pickers-palettes',
    colorCategoryLabel: 'Pickers & Palettes',
    iconBgColor: 'bg-rose-500/15',
    iconTextColor: 'text-rose-500',
    iconName: 'Dices',
    actionType: 'generic'
  },
  {
    id: 'material-color-generator',
    name: 'Material Color Generator',
    description: 'Generate Material Design 50-900 color swatches for UI components.',
    colorCategory: 'pickers-palettes',
    colorCategoryLabel: 'Pickers & Palettes',
    iconBgColor: 'bg-red-500/15',
    iconTextColor: 'text-red-500',
    iconName: 'LayoutGrid',
    actionType: 'generic'
  },
  {
    id: 'tailwind-color-generator',
    name: 'Tailwind Color Generator',
    description: 'Create custom Tailwind CSS color scales (50 to 950) with copyable config code.',
    colorCategory: 'pickers-palettes',
    colorCategoryLabel: 'Pickers & Palettes',
    iconBgColor: 'bg-sky-500/15',
    iconTextColor: 'text-sky-500',
    iconName: 'Code',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'theme-generator',
    name: 'Theme Generator',
    description: 'Build complete dark and light UI theme color schemes with preview components.',
    colorCategory: 'pickers-palettes',
    colorCategoryLabel: 'Pickers & Palettes',
    iconBgColor: 'bg-indigo-500/15',
    iconTextColor: 'text-indigo-500',
    iconName: 'SunMedium',
    actionType: 'generic'
  },
  {
    id: 'brand-palette-generator',
    name: 'Brand Palette Generator',
    description: 'Create professional brand identity color schemes with primary, secondary, and accent tones.',
    colorCategory: 'pickers-palettes',
    colorCategoryLabel: 'Pickers & Palettes',
    iconBgColor: 'bg-amber-500/15',
    iconTextColor: 'text-amber-500',
    iconName: 'Award',
    actionType: 'generic'
  },
  {
    id: 'color-wheel',
    name: 'Color Wheel',
    description: 'Interactive chromatic color wheel to explore color harmonies, tones, and angles.',
    colorCategory: 'pickers-palettes',
    colorCategoryLabel: 'Pickers & Palettes',
    iconBgColor: 'bg-purple-500/15',
    iconTextColor: 'text-purple-500',
    iconName: 'Disc',
    actionType: 'generic'
  },

  // --- COLOR CONVERTERS (4 Tools) ---
  {
    id: 'hex-rgb',
    name: 'HEX ↔ RGB',
    description: 'Convert Hexadecimal color codes (#f59e0b) to RGB (245, 158, 11) and vice-versa.',
    colorCategory: 'converters',
    colorCategoryLabel: 'Color Converters',
    iconBgColor: 'bg-amber-500/15',
    iconTextColor: 'text-amber-500',
    iconName: 'RefreshCw',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'hex-hsl',
    name: 'HEX ↔ HSL',
    description: 'Convert Hex color values to Hue, Saturation, Lightness percentage parameters.',
    colorCategory: 'converters',
    colorCategoryLabel: 'Color Converters',
    iconBgColor: 'bg-orange-500/15',
    iconTextColor: 'text-orange-500',
    iconName: 'SlidersHorizontal',
    actionType: 'generic'
  },
  {
    id: 'hex-hsv',
    name: 'HEX ↔ HSV',
    description: 'Convert Hex colors to Hue, Saturation, Value (HSB) models.',
    colorCategory: 'converters',
    colorCategoryLabel: 'Color Converters',
    iconBgColor: 'bg-yellow-500/15',
    iconTextColor: 'text-yellow-500',
    iconName: 'Sliders',
    actionType: 'generic'
  },
  {
    id: 'hex-cmyk',
    name: 'HEX ↔ CMYK',
    description: 'Translate digital web Hex colors into print Cyan, Magenta, Yellow, Key (Black) ink values.',
    colorCategory: 'converters',
    colorCategoryLabel: 'Color Converters',
    iconBgColor: 'bg-red-500/15',
    iconTextColor: 'text-red-500',
    iconName: 'Printer',
    actionType: 'generic'
  },

  // --- ACCESSIBILITY & TESTING (3 Tools) ---
  {
    id: 'contrast-checker',
    name: 'Contrast Checker',
    description: 'Check color contrast ratios against WCAG 2.1 AA and AAA standards for web accessibility.',
    colorCategory: 'accessibility',
    colorCategoryLabel: 'Accessibility & Contrast',
    iconBgColor: 'bg-emerald-500/15',
    iconTextColor: 'text-emerald-500',
    iconName: 'CheckCircle2',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'color-blind-simulator',
    name: 'Color Blind Simulator',
    description: 'Simulate Protanopia, Deuteranopia, Tritanopia, and Achromatopsia vision for color testing.',
    colorCategory: 'accessibility',
    colorCategoryLabel: 'Accessibility & Contrast',
    iconBgColor: 'bg-teal-500/15',
    iconTextColor: 'text-teal-500',
    iconName: 'Eye',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'accessible-color-checker',
    name: 'Accessible Color Checker',
    description: 'Find closest compliant accessible foreground/background text color pairs automatically.',
    colorCategory: 'accessibility',
    colorCategoryLabel: 'Accessibility & Contrast',
    iconBgColor: 'bg-blue-500/15',
    iconTextColor: 'text-blue-500',
    iconName: 'ShieldCheck',
    actionType: 'generic'
  },

  // --- UI & CSS GENERATORS (5 Tools) ---
  {
    id: 'gradient-generator',
    name: 'Gradient Generator',
    description: 'Create linear and radial multi-stop color gradients with CSS code export.',
    colorCategory: 'css-ui',
    colorCategoryLabel: 'CSS & UI Generators',
    iconBgColor: 'bg-amber-500/15',
    iconTextColor: 'text-amber-500',
    iconName: 'Sparkles',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'css-gradient',
    name: 'CSS Gradient',
    description: 'Design beautiful CSS background gradient patterns with angle controls and presets.',
    colorCategory: 'css-ui',
    colorCategoryLabel: 'CSS & UI Generators',
    iconBgColor: 'bg-orange-500/15',
    iconTextColor: 'text-orange-500',
    iconName: 'Layers',
    actionType: 'generic'
  },
  {
    id: 'glassmorphism-generator',
    name: 'Glassmorphism Generator',
    description: 'Generate modern frosted glass CSS backdrop-filter effects with blur controls.',
    colorCategory: 'css-ui',
    colorCategoryLabel: 'CSS & UI Generators',
    iconBgColor: 'bg-indigo-500/15',
    iconTextColor: 'text-indigo-500',
    iconName: 'Maximize2',
    actionType: 'generic'
  },
  {
    id: 'neumorphism-generator',
    name: 'Neumorphism Generator',
    description: 'Create soft extruded 3D UI neumorphic shadow effects with customizable light directions.',
    colorCategory: 'css-ui',
    colorCategoryLabel: 'CSS & UI Generators',
    iconBgColor: 'bg-violet-500/15',
    iconTextColor: 'text-violet-500',
    iconName: 'Box',
    actionType: 'generic'
  },
  {
    id: 'shadow-generator',
    name: 'Shadow Generator',
    description: 'Design smooth multi-layered CSS box-shadow effects with realistic depth.',
    colorCategory: 'css-ui',
    colorCategoryLabel: 'CSS & UI Generators',
    iconBgColor: 'bg-sky-500/15',
    iconTextColor: 'text-sky-500',
    iconName: 'Copy',
    actionType: 'generic'
  },
  {
    id: 'border-generator',
    name: 'Border Generator',
    description: 'Generate CSS border styles, custom outlines, rounded radii, and gradient borders.',
    colorCategory: 'css-ui',
    colorCategoryLabel: 'CSS & UI Generators',
    iconBgColor: 'bg-blue-500/15',
    iconTextColor: 'text-blue-500',
    iconName: 'Square',
    actionType: 'generic'
  },

  // --- MIXING & VARIATIONS (5 Tools) ---
  {
    id: 'color-mixer',
    name: 'Color Mixer',
    description: 'Blend two or more colors together in custom ratios to generate smooth transition steps.',
    colorCategory: 'mixing-shades',
    colorCategoryLabel: 'Shades & Mixing',
    iconBgColor: 'bg-amber-500/15',
    iconTextColor: 'text-amber-500',
    iconName: 'Blend',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'tint-generator',
    name: 'Tint Generator',
    description: 'Generate lighter tint variations of any base color by mixing with pure white.',
    colorCategory: 'mixing-shades',
    colorCategoryLabel: 'Shades & Mixing',
    iconBgColor: 'bg-yellow-500/15',
    iconTextColor: 'text-yellow-500',
    iconName: 'Sun',
    actionType: 'generic'
  },
  {
    id: 'shade-generator',
    name: 'Shade Generator',
    description: 'Generate darker shade variations of any base color by mixing with pure black.',
    colorCategory: 'mixing-shades',
    colorCategoryLabel: 'Shades & Mixing',
    iconBgColor: 'bg-stone-500/15',
    iconTextColor: 'text-amber-600',
    iconName: 'Moon',
    actionType: 'generic'
  },
  {
    id: 'opacity-calculator',
    name: 'Opacity Calculator',
    description: 'Calculate 8-digit Hex alpha transparency values (e.g. 80% = #CC) and RGBA opacity.',
    colorCategory: 'mixing-shades',
    colorCategoryLabel: 'Shades & Mixing',
    iconBgColor: 'bg-orange-500/15',
    iconTextColor: 'text-orange-500',
    iconName: 'Percent',
    actionType: 'generic'
  },
  {
    id: 'color-wheel-mix',
    name: 'Color Wheel',
    description: 'Explore color mixing and harmonic relationship intervals across standard color wheels.',
    colorCategory: 'mixing-shades',
    colorCategoryLabel: 'Shades & Mixing',
    iconBgColor: 'bg-amber-500/15',
    iconTextColor: 'text-amber-500',
    iconName: 'CircleDot',
    actionType: 'generic'
  }
];
